import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type {
  Feature,
  FeatureCollection,
  Geometry,
  GeoJsonProperties,
} from 'geojson';
import { firstValueFrom, forkJoin, Observable, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { OPERATORS, feedsConfig } from 'src/app/services/constants';
import { ApiService } from './api.service';
import { HelperService } from './helper.service';

export type ObservationFeatureProps = GeoJsonProperties & { _color?: string };

type ObservationFeedContext = {
  feed: any;
  sensor: any;
  config: any;
  isAssociated: boolean;
};

export type VesselObservationResult =
  | {
      status: 'success';
      matchedFeatures: Feature<Geometry, ObservationFeatureProps>[];
      panelConfig: any | null;
    }
  | {
      status: 'observationUnavailable' | 'dataUnavailable' | 'noMatches';
    };

export type VesselObservationRequest = {
  collection: any;
  collections?: any[];
  collectionIndex?: number;
  currentTaskId?: string;
  taskId?: string;
  currentUserId?: string | number;
  imageType: string;
  combinedScenes: boolean;
  sceneIdList: string[];
  infoserviceModelType: string;
  cachedTiffResult?: any;
  cancel$?: Observable<unknown>;
  loadSceneTiffResult: (sensorObj: any, selectedScene: any) => Promise<any>;
};

@Injectable({ providedIn: 'root' })
export class ObservationVesselService {
  constructor(
    private httpClient: HttpClient,
    private apiService: ApiService,
    private helperService: HelperService
  ) {}

  async resolveVesselLengthObservation(
    request: VesselObservationRequest,
    threshold: number,
    keepGreater: boolean
  ): Promise<VesselObservationResult> {
    const contexts = this.getObservationFeedContexts(request.collection);
    if (!contexts.length) {
      return { status: 'observationUnavailable' };
    }

    const matchedFeatures: Feature<Geometry, ObservationFeatureProps>[] = [];
    let liveFeedLoaded = false;
    let panelConfig: any | null = null;

    for (const context of contexts) {
      panelConfig = panelConfig ?? context.config;

      let featureCollection: FeatureCollection<Geometry, ObservationFeatureProps> | null = null;
      try {
        featureCollection = request.imageType === 'png'
          ? await this.fetchObservationPngFeedFeatureCollection(request, context)
          : await this.fetchObservationTiffFeedFeatureCollection(request, context);
      } catch {
        continue;
      }

      const rawFeatures = featureCollection?.features;
      const safeFeatures: Feature<Geometry, ObservationFeatureProps>[] =
        Array.isArray(rawFeatures) ? rawFeatures : [];
      if (!safeFeatures.length) {
        continue;
      }

      liveFeedLoaded = true;
      const filtered = safeFeatures.filter((feature) =>
        this.matchByLength(feature, threshold, keepGreater)
      );

      filtered.forEach((feature) => {
        matchedFeatures.push({
          ...feature,
          properties: {
            ...(feature?.properties || {}),
            _color: '#D32F2F',
          },
        });
      });
    }

    if (!panelConfig) {
      return { status: 'observationUnavailable' };
    }

    if (!liveFeedLoaded) {
      return { status: 'dataUnavailable' };
    }

    if (!matchedFeatures.length) {
      return { status: 'noMatches' };
    }

    return {
      status: 'success',
      matchedFeatures,
      panelConfig,
    };
  }

  private getObservationFeedContexts(collection: any): ObservationFeedContext[] {
    const sensors = Array.isArray(collection?.sensors) ? collection.sensors : [];
    const contexts: ObservationFeedContext[] = [];
    const seen = new Set<string>();

    for (const sensor of sensors) {
      const feedGroups = [
        {
          feeds: Array.isArray(sensor?.InfoServices) ? sensor.InfoServices : [],
          isAssociated: false,
        },
        {
          feeds: Array.isArray(sensor?.AssociatedFeeds)
            ? sensor.AssociatedFeeds
            : [],
          isAssociated: true,
        },
      ];

      for (const group of feedGroups) {
        for (const feed of group.feeds) {
          const config = feedsConfig[feed?._id];
          if (!config || !this.isVesselType(config?.type)) {
            continue;
          }

          const key = this.getFeedKey(feed, config);
          if (seen.has(key)) {
            continue;
          }

          seen.add(key);
          contexts.push({
            feed,
            sensor,
            config,
            isAssociated: group.isAssociated,
          });
        }
      }
    }

    return contexts;
  }

  private async fetchObservationGeoJsonFromUrls(
    urls: string[],
    cancel$?: Observable<unknown>
  ): Promise<any | null> {
    if (!urls?.length) {
      return null;
    }

    const request$ = forkJoin(urls.map((url) => this.httpClient.get(url)));
    const geoJsonDataArray = await firstValueFrom(
      cancel$ ? request$.pipe(takeUntil(cancel$)) : request$
    );

    return this.combineGeoJsonFeatures(geoJsonDataArray as any[]);
  }

  private normalizeObservationFeatureCollection(
    data: any,
    config: any
  ): FeatureCollection<Geometry, ObservationFeatureProps> | null {
    const featureCollection = this.asFeatureCollection(
      data
    ) as FeatureCollection<Geometry, ObservationFeatureProps>;

    if (!featureCollection?.features?.length) {
      return null;
    }

    if (config?.filterKey) {
      return this.getFilteredData(
        featureCollection,
        config.filterKey
      ) as FeatureCollection<Geometry, ObservationFeatureProps>;
    }

    return featureCollection;
  }

  private async fetchObservationPngFeedFeatureCollection(
    request: VesselObservationRequest,
    context: ObservationFeedContext
  ): Promise<FeatureCollection<Geometry, ObservationFeatureProps> | null> {
    const { feed, sensor, config, isAssociated } = context;
    const activeScene = this.getActiveSceneForSensor(
      sensor,
      request.collection,
      request.collections,
      request.collectionIndex
    );
    const activeSceneId = activeScene?.id;
    const taskId = request.collection?.taskId ?? request.currentTaskId ?? request.taskId;
    const observationOrFeed = sensor?.Observation?.length
      ? sensor.Observation[0]
      : feed;

    if (observationOrFeed?.modelType === request.infoserviceModelType) {
      let payload: any;
      if (sensor?.operatorKey === OPERATORS.AXELSPACE) {
        payload = {
          action:
            request.combinedScenes || isAssociated ? 'sensorFeed' : 'ISDisplay',
          feedId: feed?._id,
          type: 'json',
          ...(isAssociated ? {} : { sceneId: sensor.id }),
          sensorId: isAssociated ? feed?.sensorId : sensor.id,
        };
      } else {
        payload = {
          action:
            request.combinedScenes || isAssociated ? 'sensorFeed' : 'ISDisplay',
          feedId: feed?._id,
          type: 'json',
          ...(isAssociated || !activeSceneId ? {} : { sceneId: activeSceneId }),
          sensorId: isAssociated ? feed?.sensorId : sensor.id,
        };
      }

      const collectionImage$ = this.apiService.getCollectionImage(taskId, payload);
      const res: any = await firstValueFrom(
        request.cancel$ ? collectionImage$.pipe(takeUntil(request.cancel$)) : collectionImage$
      );
      const combinedGeoJson = await this.fetchObservationGeoJsonFromUrls(
        res?.result?.feedData ?? [],
        request.cancel$
      );

      return this.normalizeObservationFeatureCollection(combinedGeoJson, config);
    }

    if (request.combinedScenes || isAssociated) {
      const payload = {
        customerid: request.currentUserId,
        feedId: feed?._id,
        taskId,
        sensorId: isAssociated ? feed?.sensorId : sensor.id,
      };

      const objectDetection$ = this.apiService.getObjectDetectionData(payload);
      const res: any = await firstValueFrom(
        request.cancel$ ? objectDetection$.pipe(takeUntil(request.cancel$)) : objectDetection$
      );

      return this.normalizeObservationFeatureCollection(
        res?.result?.feedData,
        config
      );
    }

    const cached = Array.isArray(feed?.insightDatas) ? feed.insightDatas : [];
    if (!cached.length) {
      return null;
    }

    if (sensor?.operatorKey === OPERATORS.AXELSPACE) {
      return this.normalizeObservationFeatureCollection(cached[0], config);
    }

    if (activeSceneId) {
      const sceneInsight = cached.find(
        (sceneData: any) => sceneData?.sceneId === activeSceneId
      );
      return this.normalizeObservationFeatureCollection(sceneInsight, config);
    }

    const matchingInsights = (request.sceneIdList ?? [])
      .map((sceneId) =>
        cached.find((sceneData: any) => sceneData?.sceneId === sceneId)
      )
      .filter((sceneData: any) => !!sceneData);

    if (!matchingInsights.length) {
      return null;
    }

    const merged = this.combineGeoJsonFeatures(
      matchingInsights
        .map((sceneData: any) => this.asFeatureCollection(sceneData))
        .filter((sceneData: any) => sceneData?.features?.length)
    );

    return this.normalizeObservationFeatureCollection(merged, config);
  }

  private async fetchObservationTiffFeedFeatureCollection(
    request: VesselObservationRequest,
    context: ObservationFeedContext
  ): Promise<FeatureCollection<Geometry, ObservationFeatureProps> | null> {
    const { feed, sensor, config, isAssociated } = context;
    const taskId = request.currentTaskId ?? request.collection?.taskId ?? request.taskId;
    let result: any = null;

    if (request.combinedScenes || isAssociated) {
      const payload = {
        customerid: request.currentUserId,
        feedId: feed?._id,
        taskId,
        sensorId: isAssociated ? feed?.sensorId ?? sensor.id : sensor.id,
      };

      const geoCollection$ = this.apiService.getFeedFromGeoCollection(payload);
      const res: any = await firstValueFrom(
        request.cancel$ ? geoCollection$.pipe(takeUntil(request.cancel$)) : geoCollection$
      );

      if (res?.msg === 'Failed') {
        return null;
      }

      result = res?.result;
    } else if (sensor?.scenes?.filter((scene: any) => scene.checked == true).length === 1) {
      const selectedScene = sensor.scenes.find((scene: any) => scene.checked === true);
      if (!selectedScene?.id) {
        return null;
      }

      const sensorObj = {
        sensorId: sensor.id,
        sceneId: selectedScene.id,
        action: 'scene',
        fileType: selectedScene.imageType,
      };

      result = await request.loadSceneTiffResult(sensorObj, selectedScene);
    } else {
      result = request.cachedTiffResult;
    }

    if (!result?.layer?.shapes?.length) {
      return null;
    }

    const matchedShape = result.layer.shapes.find((shape: any) =>
      shape?.sceneId ? shape.sceneId === feed?.sceneId : shape?.id === feed?._id
    );
    if (!matchedShape) {
      return null;
    }

    const classFilter = this.helperService.classCqlStrict(config?.class, 'class');
    const baseCql = this.helperService.combineFilters(matchedShape.cql, classFilter);
    const wfsBase = environment.geoServerUrl.replace(/\/wms\b/i, '/wfs');
    const qs = this.helperService.makeWfsParams(
      matchedShape.layerName ?? matchedShape.layername
    );
    if (baseCql) {
      qs.set('CQL_FILTER', baseCql);
    }

    const geoJson$ = this.httpClient.get<any>(`${wfsBase}?${qs.toString()}`);
    const geojson = await firstValueFrom(
      request.cancel$ ? geoJson$.pipe(takeUntil(request.cancel$)) : geoJson$
    );

    return this.normalizeObservationFeatureCollection(geojson, config);
  }

  private getCheckedValidScenes(sensor: any): any[] {
    return Array.isArray(sensor?.scenes)
      ? sensor.scenes.filter(
          (scene: any) =>
            scene?.id && scene?.status !== 'failed' && scene?.checked === true
        )
      : [];
  }

  private getActiveSceneForSensor(
    sensor: any,
    collection: any,
    collections?: any[],
    collectionIndex?: number
  ): any {
    const sensorScene = this.getCheckedValidScenes(sensor)[0];
    if (sensorScene) {
      return sensorScene;
    }

    const sensors = collection?.sensors ?? collections?.[collectionIndex ?? -1]?.sensors ?? [];
    for (const currentSensor of sensors) {
      const fallbackScene = this.getCheckedValidScenes(currentSensor)[0];
      if (fallbackScene) {
        return fallbackScene;
      }
    }

    return null;
  }

  private getFeedKey(feed: any, config: any): string {
    return String(
      feed?._id ?? feed?.isInfo?._id ?? feed?.id ?? config?.type ?? Date.now()
    );
  }

  private isVesselType(type?: string): boolean {
    return ['ship', 'vessel'].includes(String(type ?? '').toLowerCase());
  }

  private matchByLength(f: any, len: number, keepGreater: boolean): boolean {
    const value =
      parseFloat(
        (f?.properties?.vessel_length ?? f?.properties?.vessel_len ?? '')
          .toString()
          .replace(/[^\d.]/g, '')
      ) || 0;

    if (Number.isNaN(value)) {
      return false;
    }

    return keepGreater ? value > len : value < len;
  }

  private combineGeoJsonFeatures(geoJsonDataArray: any[]): any {
    const allFeatures = geoJsonDataArray.flatMap((geoJson) => geoJson.features);
    return {
      type: 'FeatureCollection',
      features: allFeatures,
      metadata: geoJsonDataArray[0]?.metadata,
      feed_results: geoJsonDataArray[0]?.feed_results,
    };
  }

  private asFeatureCollection(x: any): FeatureCollection {
    if (x?.type === 'FeatureCollection') {
      return x;
    }

    if (x?.insightFootprint?.type === 'FeatureCollection') {
      return x.insightFootprint;
    }

    const features = x?.insightFootprint?.features ?? x?.features ?? [];
    return { type: 'FeatureCollection', features };
  }

  private getFilteredData(jsonData: any, className: string) {
    const filteredFeatures = jsonData.features.filter(
      (feature: any) => feature?.properties?.class != className
    );

    return {
      type: jsonData.type,
      crs: jsonData.crs,
      features: filteredFeatures,
      metadata: jsonData?.metadata,
      feed_results: jsonData?.feed_results,
    };
  }
}